import { getById } from "../../../store/course/courseBaseApi";
import {
  assignCourse,
  getCourseAssignments,
} from "../../../store/study/studyBaseApi";
import { APPLICATION_NAME, nowISO } from "../../../utils";

/**
 * Idempotently assigns a course to the student, awaiting the POST.
 *
 * Login opens the LAST entry of the student's course assignments, so a
 * level-up is only durable once the new course is in that list. The backend
 * (saveCourseAssignment) merge-APPENDS with no dedup, hence the
 * check-before-assign; and the old saga dispatch was fire-and-forget, which
 * let the hard navigation after level-up race (and sometimes lose) the POST —
 * hence the awaited direct API calls.
 *
 * Returns true when the course is assigned (already or now), false when the
 * assign could not be confirmed — callers may proceed anyway; the backend
 * next-ir-level self-heal recovers the student at next login.
 */
export const ensureCourseAssigned = async (studentId, courseId) => {
  try {
    const resp = await getCourseAssignments({ userId: studentId });
    const existing = resp?.data?.courses || [];
    if (existing.some((c) => c?.courseId === courseId)) {
      console.log("[assign] course already assigned — skipping", courseId);
      return true;
    }
  } catch (e) {
    // Couldn't read the current list — fall through and assign; worst case
    // is a duplicate entry, which login handles (last entry wins).
    console.warn("[assign] could not read existing assignments", e);
  }

  const { data: courseData } = await getById({ id: courseId });
  if (!courseData?.id) {
    console.error("[assign] course not found — cannot assign", courseId);
    return false;
  }

  const now = nowISO();
  await assignCourse({
    body: {
      application: APPLICATION_NAME,
      assigneeRole: "student",
      assignedUserId: studentId,
      courses: [
        {
          courseId: courseData.id,
          courseName: courseData.courseName,
          courseCode: courseData.code,
          dateEnrolled: now,
          assignedBy: studentId,
          assignedDate: now,
          assignedMethod: "",
          currentStatus: "ASSIGNED",
        },
      ],
    },
  });
  console.log("[assign] ✅ assigned course", courseId, "to", studentId);
  return true;
};
